import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Plus, Trash2, ArrowLeft, Sparkles, PenLine, Search, Database as DbIcon, ChevronDown, ChevronRight, LayoutDashboard, MessageSquare, X, Download, FileDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import html2canvas from 'html2canvas';
import './DashboardPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const COLORS = ['#0ea5e9', '#06b6d4', '#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1', '#f97316'];
const GOOGLE_CLIENT_ID = '565978510210-91mmqll7vteaai602ekfg0g8m78c9ioj.apps.googleusercontent.com';

// =================== Utility Functions ===================

const sanitizeFilename = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remover acentos
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50); // Limitar longitud
};

const exportWidgetToImage = async (widgetRef, title) => {
  if (!widgetRef.current) return;
  
  try {
    const canvas = await html2canvas(widgetRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
    });
    
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${sanitizeFilename(title)}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    });
  } catch (err) {
    console.error('Error exportando imagen:', err);
    alert('Error al exportar la imagen');
  }
};

const exportWidgetToCSV = (data, title) => {
  if (!data || !data.length) {
    alert('No hay datos para exportar');
    return;
  }
  
  try {
    // Obtener todas las columnas únicas
    const allKeys = [...new Set(data.flatMap(row => Object.keys(row)))];
    
    // Crear CSV content
    const headers = allKeys.join(',');
    const rows = data.map(row => 
      allKeys.map(key => {
        const value = row[key] ?? '';
        // Escapar comillas y encerrar en comillas si contiene coma o salto de línea
        const stringValue = String(value).replace(/"/g, '""');
        return stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')
          ? `"${stringValue}"`
          : stringValue;
      }).join(',')
    );
    
    const csvContent = [headers, ...rows].join('\n');
    
    // Crear blob con BOM para Excel
    const blob = new Blob(['\ufeff' + csvContent], { 
      type: 'text/csv;charset=utf-8;' 
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${sanitizeFilename(title)}-datos.csv`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error exportando CSV:', err);
    alert('Error al exportar los datos');
  }
};

// =================== Google Integration Functions ===================

const googleApi = {
  async getAuthUrl(redirectUri) {
    const r = await fetch(`${BACKEND_URL}/api/google/auth/url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ redirect_uri: redirectUri })
    });
    return r.json();
  },
  
  async exchangeCode(code, state, redirectUri) {
    const r = await fetch(`${BACKEND_URL}/api/google/auth/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state, redirect_uri: redirectUri })
    });
    return r.json();
  },
  
  async exportToSlides(credentials, imageBase64, title, folderId = null) {
    const r = await fetch(`${BACKEND_URL}/api/google/export/slides`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credentials,
        image_base64: imageBase64,
        title,
        folder_id: folderId
      })
    });
    return r.json();
  },
  
  async exportToSheets(credentials, data, title, folderId = null) {
    const r = await fetch(`${BACKEND_URL}/api/google/export/sheets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credentials,
        data,
        title,
        folder_id: folderId
      })
    });
    return r.json();
  },
  
  async createWorkspaceFolder(credentials, folderName, existingFolderId = null) {
    const r = await fetch(`${BACKEND_URL}/api/google/folder/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credentials,
        folder_name: folderName,
        existing_folder_id: existingFolderId
      })
    });
    return r.json();
  }
};

// =================== API ===================

const dashApi = {
  async list() {
    const r = await fetch(`${BACKEND_URL}/api/dashboards`);
    return r.json();
  },
  async create() {
    const r = await fetch(`${BACKEND_URL}/api/dashboards`, { method: 'POST' });
    return r.json();
  },
  async get(id) {
    const r = await fetch(`${BACKEND_URL}/api/dashboards/${id}`);
    return r.json();
  },
  async remove(id) {
    await fetch(`${BACKEND_URL}/api/dashboards/${id}`, { method: 'DELETE' });
  },
  async removeWidget(dashId, idx) {
    const r = await fetch(`${BACKEND_URL}/api/dashboards/${dashId}/widgets/${idx}`, { method: 'DELETE' });
    return r.json();
  },
  streamChat(dashboardId, message, onEvent) {
    const controller = new AbortController();
    fetch(`${BACKEND_URL}/api/dashboard/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dashboard_id: dashboardId, message }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) { onEvent({ type: 'error', error: 'Error de conexi\u00f3n' }); return; }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let gotDone = false;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                onEvent(data);
                if (data.type === 'done') gotDone = true;
              } catch (e) { /* ignore */ }
            }
          }
        }
        if (!gotDone) onEvent({ type: 'done' });
      })
      .catch((err) => { if (err.name !== 'AbortError') onEvent({ type: 'error', error: err.message }); });
    return controller;
  },
};

// =================== Widget Renderer ===================

function DashboardWidget({ widget, index, onRemove, googleCredentials, onGoogleAuth, onChatMessage, ensureWorkspaceFolder }) {
  const widgetRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportImage = () => {
    exportWidgetToImage(widgetRef, widget.title || 'widget');
  };

  const handleExportCSV = () => {
    exportWidgetToCSV(widget.data, widget.title || 'widget');
  };

  const handleExportToSlides = async () => {
    if (!googleCredentials) {
      onGoogleAuth('slides', widget);
      return;
    }

    setIsExporting(true);
    onChatMessage && onChatMessage('Exportando a Google Slides...', 'system');
    
    try {
      // Ensure workspace folder exists
      const folderId = await ensureWorkspaceFolder();
      
      // Capture widget as image
      const canvas = await html2canvas(widgetRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      
      const imageBase64 = canvas.toDataURL('image/png');
      
      // Export to Google Slides
      const result = await googleApi.exportToSlides(
        googleCredentials,
        imageBase64,
        widget.title || 'Widget',
        folderId
      );
      
      if (result.success) {
        // Open the presentation
        window.open(result.url, '_blank');
        onChatMessage && onChatMessage(
          `Presentación creada: **"${result.title}"**\n\n[Abrir en Google Slides](${result.url})`, 
          'success'
        );
      } else {
        onChatMessage && onChatMessage(`Error: ${result.error}`, 'error');
      }
    } catch (err) {
      console.error('Error exporting to Slides:', err);
      onChatMessage && onChatMessage('Error al exportar a Google Slides', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportToSheets = async () => {
    if (!googleCredentials) {
      onGoogleAuth('sheets', widget);
      return;
    }

    if (!widget.data || !widget.data.length) {
      onChatMessage && onChatMessage('No hay datos para exportar', 'error');
      return;
    }

    setIsExporting(true);
    onChatMessage && onChatMessage('Exportando a Google Sheets...', 'system');
    
    try {
      // Ensure workspace folder exists
      const folderId = await ensureWorkspaceFolder();
      
      const result = await googleApi.exportToSheets(
        googleCredentials,
        widget.data,
        widget.title || 'Widget',
        folderId
      );
      
      if (result.success) {
        // Open the spreadsheet
        window.open(result.url, '_blank');
        onChatMessage && onChatMessage(
          `Hoja de cálculo creada: **"${result.title}"** (${result.rows} filas)\n\n[Abrir en Google Sheets](${result.url})`, 
          'success'
        );
      } else {
        onChatMessage && onChatMessage(`Error: ${result.error}`, 'error');
      }
    } catch (err) {
      console.error('Error exporting to Sheets:', err);
      onChatMessage && onChatMessage('Error al exportar a Google Sheets', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const renderChart = () => {
    const data = widget.data || [];
    if (!data.length) return <p className="dw-empty">Sin datos</p>;

    switch (widget.type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey={widget.x_key || 'label'} fontSize={11} tick={{ fill: '#71717a' }} />
              <YAxis fontSize={11} tick={{ fill: '#71717a' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e4e4e7', fontSize: '12px' }} />
              <Bar dataKey={widget.y_key || 'value'} radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey={widget.x_key || 'name'} cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e4e4e7', fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey={widget.x_key || 'label'} fontSize={11} tick={{ fill: '#71717a' }} />
              <YAxis fontSize={11} tick={{ fill: '#71717a' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e4e4e7', fontSize: '12px' }} />
              <Line type="monotone" dataKey={widget.y_key || 'value'} stroke="#0ea5e9" strokeWidth={2} dot={{ fill: '#0ea5e9', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'kpi':
        return (
          <div className="dw-kpi-grid">
            {data.map((item, i) => (
              <div key={i} className="dw-kpi-item">
                <span className="dw-kpi-value">{typeof item.value === 'number' ? item.value.toLocaleString() : item.value}</span>
                <span className="dw-kpi-label">{item.label || item.name}</span>
              </div>
            ))}
          </div>
        );

      case 'table':
        if (!data.length) return <p className="dw-empty">Sin datos</p>;
        const cols = Object.keys(data[0]);
        return (
          <div className="dw-table-wrap">
            <table className="dw-table">
              <thead>
                <tr>{cols.map(c => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {data.slice(0, 20).map((row, i) => (
                  <tr key={i}>{cols.map(c => <td key={c}>{row[c]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      default:
        return <p className="dw-empty">Tipo de widget no soportado: {widget.type}</p>;
    }
  };

  return (
    <motion.div
      ref={widgetRef}
      className="dw-card"
      data-testid="dashboard-widget"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="dw-card-header">
        <h3 className="dw-card-title">{widget.title || 'Widget'}</h3>
        <div className="dw-card-actions">
          <button 
            className="dw-card-action" 
            onClick={handleExportImage} 
            title="Descargar como imagen (PNG)"
            data-testid="export-image-btn"
            disabled={isExporting}
          >
            <Download size={14} />
          </button>
          <button 
            className="dw-card-action" 
            onClick={handleExportCSV} 
            title="Descargar datos (CSV)"
            data-testid="export-csv-btn"
            disabled={isExporting}
          >
            <FileDown size={14} />
          </button>
          <button 
            className="dw-card-action dw-card-action-google" 
            onClick={handleExportToSlides} 
            title="Enviar a Google Slides"
            data-testid="export-google-slides-btn"
            disabled={isExporting}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M8,15.5V18H16V15.5L12,11.5L8,15.5Z"/>
            </svg>
          </button>
          <button 
            className="dw-card-action dw-card-action-google" 
            onClick={handleExportToSheets} 
            title="Enviar a Google Sheets"
            data-testid="export-google-sheets-btn"
            disabled={isExporting}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,11H15V15H19M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3H19M5,7V19H19V7H5M11,9H13V11H11V9M11,13H13V15H11V13M7,9H9V11H7V9M7,13H9V15H7V13Z"/>
            </svg>
          </button>
          <button 
            className="dw-card-remove" 
            onClick={() => onRemove(index)} 
            title="Eliminar widget"
            data-testid="remove-widget-btn"
            disabled={isExporting}
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {widget.description && <p className="dw-card-desc">{widget.description}</p>}
      <div className="dw-card-chart">
        {renderChart()}
      </div>
    </motion.div>
  );
}

// =================== Thinking Panel (compact) ===================

function MiniThinking({ steps, isActive }) {
  const [expanded, setExpanded] = useState(true);
  if (!steps.length) return null;
  return (
    <div className="db-thinking">
      <button className="db-thinking-toggle" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Sparkles size={12} />
        <span>{isActive ? 'Pensando...' : `${steps.length} pasos`}</span>
        {isActive && <span className="db-thinking-spinner" />}
      </button>
      {expanded && (
        <div className="db-thinking-steps">
          {steps.map((s, i) => (
            <div key={i} className="db-thinking-step">
              <span className="db-thinking-dot" />
              <span className="db-thinking-text">{s.title}{s.detail ? `: ${s.detail.substring(0, 80)}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =================== Main Dashboard Page ===================

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboards, setDashboards] = useState([]);
  const [activeDashId, setActiveDashId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [error, setError] = useState(null);
  const [expertProgress, setExpertProgress] = useState(null); // { current: 1, total: 6, status: 'generating' }
  const [googleCredentials, setGoogleCredentials] = useState(null);
  const [pendingGoogleExport, setPendingGoogleExport] = useState(null); // { type: 'slides'|'sheets', widget }
  const [driveFolderId, setDriveFolderId] = useState(null); // Google Drive workspace folder ID

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const streamTextRef = useRef('');

  useEffect(() => { 
    loadDashboards();
    // Check if returning from OAuth
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code && state) {
      handleOAuthCallback(code, state);
    }
    // Load credentials from localStorage
    const savedCreds = localStorage.getItem('google_credentials');
    if (savedCreds) {
      try {
        setGoogleCredentials(JSON.parse(savedCreds));
      } catch (e) {
        console.error('Error loading saved credentials:', e);
      }
    }
  }, [location]);

  const handleOAuthCallback = async (code, state) => {
    try {
      const redirectUri = `${window.location.origin}/oauth-callback`;
      const result = await googleApi.exchangeCode(code, state, redirectUri);
      if (result.credentials) {
        setGoogleCredentials(result.credentials);
        localStorage.setItem('google_credentials', JSON.stringify(result.credentials));
        
        // Clear URL params and redirect back to dashboard
        window.history.replaceState({}, document.title, '/dashboard');
        
        // Add success message to chat (will be saved to DB)
        await addChatMessage(
          '**Google Drive conectado exitosamente**\n\nAhora puedes exportar widgets directamente a Google Slides y Sheets.',
          'system'
        );
        
        // If there was a pending export, retry it
        if (pendingGoogleExport) {
          setPendingGoogleExport(null);
        }
      }
    } catch (err) {
      console.error('OAuth callback error:', err);
      await addChatMessage(
        'Error en la autorización de Google. Por favor, inténtalo de nuevo.',
        'error'
      );
    }
  };

  const addChatMessage = async (content, type = 'system') => {
    const role = type === 'error' ? 'system' : type === 'success' ? 'system' : 'system';
    const message = {
      role,
      content,
      timestamp: new Date().toISOString(),
      messageType: type
    };
    
    // Add to local state
    setMessages(prev => [...prev, message]);
    
    // Save to database if we have an active dashboard
    if (activeDashId) {
      try {
        await fetch(`${BACKEND_URL}/api/dashboards/message/system`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dashboard_id: activeDashId,
            content
          })
        });
      } catch (err) {
        console.error('Error saving system message:', err);
      }
    }
  };

  const ensureWorkspaceFolder = async () => {
    if (!googleCredentials) return null;
    if (driveFolderId) return driveFolderId;

    try {
      const dashTitle = dashboards.find(d => d.id === activeDashId)?.title || 'Dashboard';
      const folderName = `LDM - ${dashTitle}`;
      
      const result = await googleApi.createWorkspaceFolder(
        googleCredentials,
        folderName,
        driveFolderId
      );

      if (result.success) {
        setDriveFolderId(result.folder_id);
        
        if (result.created) {
          addChatMessage(
            `**Carpeta de trabajo creada en Google Drive**\n\nNombre: "${result.folder_name}"\n\n[Abrir carpeta](${result.folder_url})`,
            'system'
          );
        }
        
        return result.folder_id;
      }
    } catch (err) {
      console.error('Error creating workspace folder:', err);
    }
    
    return null;
  };

  const handleGoogleAuth = async (exportType, widget) => {
    setPendingGoogleExport({ type: exportType, widget });
    
    try {
      const redirectUri = `${window.location.origin}/oauth-callback`;
      const result = await googleApi.getAuthUrl(redirectUri);
      
      if (result.authorization_url && result.state) {
        // Store state temporarily
        sessionStorage.setItem('oauth_state', result.state);
        // Redirect to Google OAuth
        window.location.href = result.authorization_url;
      }
    } catch (err) {
      console.error('Error initiating OAuth:', err);
      alert('Error al iniciar autorización de Google');
    }
  };

  const loadDashboards = async () => {
    try { setDashboards(await dashApi.list()); } catch (e) { console.error(e); }
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { if (isStreaming) scrollToBottom(); }, [messages, isStreaming, scrollToBottom]);

  const loadDashboard = async (id) => {
    try {
      const dash = await dashApi.get(id);
      setActiveDashId(id);
      setMessages(dash.messages || []);
      setWidgets(dash.widgets || []);
      setError(null);
      setTimeout(scrollToBottom, 100);
    } catch (e) { setError('Error al cargar dashboard'); }
  };

  const handleNewDashboard = async () => {
    try {
      const dash = await dashApi.create();
      setDashboards(prev => [dash, ...prev]);
      setActiveDashId(dash.id);
      setMessages([]);
      setWidgets([]);
    } catch (e) { setError('Error al crear dashboard'); }
  };

  const handleDeleteDashboard = async (e, id) => {
    e.stopPropagation();
    try {
      await dashApi.remove(id);
      setDashboards(prev => prev.filter(d => d.id !== id));
      if (activeDashId === id) { setActiveDashId(null); setMessages([]); setWidgets([]); }
    } catch (e) { console.error(e); }
  };

  const handleRemoveWidget = async (idx) => {
    if (!activeDashId) return;
    try {
      const result = await dashApi.removeWidget(activeDashId, idx);
      setWidgets(result.widgets);
    } catch (e) { console.error(e); }
  };

  const EXPERT_WIDGETS = [
    {
      prompt: "Genera un widget KPI que muestre el total de miembros del equipo. Consulta la base de datos y devuelve el número real. Incluye el widget en un bloque ```widget con type: 'kpi', title apropiado y data con el valor.",
      name: "Total de Miembros"
    },
    {
      prompt: "Genera un gráfico de barras que muestre las tareas agrupadas por estado. Consulta la base de datos y agrupa por estado. Incluye el widget en un bloque ```widget con type: 'bar', mostrando los estados más relevantes.",
      name: "Tareas por Estado"
    },
    {
      prompt: "Genera un gráfico de pie que muestre la distribución de tareas por prioridad. Consulta la base de datos. Incluye el widget en un bloque ```widget con type: 'pie', con data que tenga 'name' y 'value'.",
      name: "Distribución por Prioridad"
    },
    {
      prompt: "Genera un widget KPI que muestre el total de tareas y el total de subtareas. Consulta la base de datos. Incluye el widget en un bloque ```widget con type: 'kpi' y data con 2 valores: uno para tareas y otro para subtareas.",
      name: "Tareas y Subtareas"
    },
    {
      prompt: "Genera un gráfico de barras con el top 5 de espacios de trabajo que tienen más tareas. Consulta la base de datos y agrupa por espacio. Incluye el widget en un bloque ```widget con type: 'bar'.",
      name: "Top 5 Espacios"
    },
    {
      prompt: "Genera una tabla con los 10 miembros del equipo más recientes. Consulta la base de datos ordenando por fecha de incorporación. Incluye el widget en un bloque ```widget con type: 'table'.",
      name: "Miembros Recientes"
    }
  ];

  const handleExpertDashboard = async () => {
    if (isStreaming || expertProgress) return;
    
    let dashId = activeDashId;
    if (!dashId) {
      try {
        const dash = await dashApi.create();
        setDashboards(prev => [dash, ...prev]);
        dashId = dash.id;
        setActiveDashId(dash.id);
      } catch (e) {
        setError('Error al crear dashboard');
        return;
      }
    }

    setExpertProgress({ current: 0, total: EXPERT_WIDGETS.length, status: 'starting' });
    setError(null);

    // Generar widgets secuencialmente
    for (let i = 0; i < EXPERT_WIDGETS.length; i++) {
      const widget = EXPERT_WIDGETS[i];
      setExpertProgress({ current: i + 1, total: EXPERT_WIDGETS.length, status: 'generating', name: widget.name });

      try {
        await new Promise((resolve, reject) => {
          let completed = false;
          const timeout = setTimeout(() => {
            if (!completed) {
              reject(new Error('Timeout generando widget'));
            }
          }, 60000); // 60 segundos por widget

          dashApi.streamChat(dashId, widget.prompt, (event) => {
            if (event.type === 'widget') {
              setWidgets(prev => [...prev, event.widget]);
            }
            if (event.type === 'done') {
              completed = true;
              clearTimeout(timeout);
              resolve();
            }
            if (event.type === 'error') {
              completed = true;
              clearTimeout(timeout);
              reject(new Error(event.error));
            }
          });
        });
        
        // Pequeña pausa entre widgets
        await new Promise(r => setTimeout(r, 500));
        
      } catch (err) {
        console.error(`Error generando widget ${i + 1}:`, err);
        setExpertProgress({ current: i + 1, total: EXPERT_WIDGETS.length, status: 'error', name: widget.name, error: err.message });
        await new Promise(r => setTimeout(r, 2000));
        // Continuar con el siguiente widget a pesar del error
      }
    }

    setExpertProgress({ current: EXPERT_WIDGETS.length, total: EXPERT_WIDGETS.length, status: 'completed' });
    loadDashboards();
    
    // Limpiar el progreso después de 3 segundos
    setTimeout(() => {
      setExpertProgress(null);
    }, 3000);
  };

  const handleSend = async (text) => {
    const msg = text || inputValue.trim();
    if (!msg || isStreaming) return;
    setError(null);

    let dashId = activeDashId;
    if (!dashId) {
      try {
        const dash = await dashApi.create();
        setDashboards(prev => [dash, ...prev]);
        dashId = dash.id;
        setActiveDashId(dash.id);
      } catch (e) { setError('Error al crear dashboard'); return; }
    }

    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInputValue('');
    setIsStreaming(true);
    setThinkingSteps([]);
    streamTextRef.current = '';
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    // Timeout de 90 segundos para detectar si el agente no responde
    const timeoutId = setTimeout(() => {
      if (isStreaming) {
        console.warn('Timeout: Stream excedió 90 segundos');
        setIsStreaming(false);
        setThinkingSteps([]);
        setError('La consulta está tomando demasiado tiempo. Esto puede deberse a una consulta muy compleja o límites de tasa de la API. Por favor, simplifica tu consulta o espera unos minutos.');
        addChatMessage('Timeout: La consulta excedió el tiempo máximo de espera (90 segundos). Intenta simplificar tu pregunta o divídela en pasos más pequeños.', 'error');
      }
    }, 90000); // 90 segundos

    let hasReceivedData = false;

    try {
      dashApi.streamChat(dashId, msg, (event) => {
        hasReceivedData = true;
        clearTimeout(timeoutId); // Limpiar timeout si recibimos datos
        
        switch (event.type) {
          case 'thinking_step':
            setThinkingSteps(prev => [...prev, event]);
            break;
          case 'status':
            break;
          case 'content':
            streamTextRef.current += event.text;
            const cur = streamTextRef.current;
            setMessages(prev => {
              const u = [...prev];
              if (u[u.length - 1]?.role === 'assistant') u[u.length - 1] = { ...u[u.length - 1], content: cur };
              return u;
            });
            break;
          case 'widget':
            setWidgets(prev => [...prev, event.widget]);
            break;
          case 'title_update':
            setDashboards(prev => prev.map(d => d.id === dashId ? { ...d, title: event.title } : d));
            break;
          case 'error':
            clearTimeout(timeoutId);
            setError(event.error);
            setIsStreaming(false);
            setThinkingSteps([]);
            addChatMessage(`Error del sistema: ${event.error}`, 'error');
            break;
          case 'done':
            clearTimeout(timeoutId);
            setIsStreaming(false);
            setThinkingSteps([]);
            loadDashboards();
            break;
          default: break;
        }
      });
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Error en stream:', err);
      setIsStreaming(false);
      setThinkingSteps([]);
      setError('Error de conexión. Por favor, verifica tu conexión e inténtalo de nuevo.');
      addChatMessage('Error de conexión con el servidor. Por favor, inténtalo de nuevo.', 'error');
    }

    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };
  const handleInput = (e) => { setInputValue(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; };

  const suggestions = [
    'Muéstrame las tareas por estado en un gráfico de barras',
    'KPI: total de miembros del equipo',
    'Gráfico de pie con tareas por prioridad',
    'Tabla con los 10 espacios de trabajo',
  ];

  return (
    <div className="db-shell">
      {/* Sidebar */}
      <aside className="db-sidebar" data-testid="dashboard-sidebar">
        <div className="db-sidebar-header">
          <button className="db-back" onClick={() => navigate('/')} data-testid="back-to-labs">
            <ArrowLeft size={16} /> Labs
          </button>
          <div className="db-header-actions">
            {/* Google Drive Status Indicator */}
            <div 
              className={`db-drive-indicator ${googleCredentials ? 'connected' : 'disconnected'}`}
              title={googleCredentials ? 'Google Drive conectado' : 'Google Drive no conectado'}
              data-testid="drive-indicator"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.71,3.5L1.15,15L4.58,21L11.13,9.5M9.73,15L6.3,21H19.42L22.85,15M22.28,14L15.42,2H8.58L8.57,2L15.43,13.5"/>
              </svg>
              {googleCredentials && (
                <span className="db-drive-dot"></span>
              )}
            </div>
            <button className="db-new-btn" onClick={handleNewDashboard} data-testid="new-dashboard-btn">
              <Plus size={16} />
            </button>
          </div>
        </div>
        <div className="db-sidebar-list">
          {dashboards.length === 0 ? (
            <div className="db-sidebar-empty">
              <LayoutDashboard size={20} />
              <span>Sin dashboards aún</span>
            </div>
          ) : dashboards.map(d => (
            <div
              key={d.id}
              className={`db-sidebar-item ${activeDashId === d.id ? 'active' : ''}`}
              onClick={() => loadDashboard(d.id)}
              data-testid="dashboard-sidebar-item"
            >
              <div className="db-sidebar-item-info">
                <span className="db-sidebar-item-title">{d.title}</span>
                <span className="db-sidebar-item-meta">{d.widget_count || 0} widgets</span>
              </div>
              <button className="db-sidebar-item-del" onClick={(e) => handleDeleteDashboard(e, d.id)}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Main area: split into dashboard canvas and chat */}
      <div className="db-main">
        {/* Dashboard Canvas */}
        <div className="db-canvas" data-testid="dashboard-canvas">
          {widgets.length === 0 ? (
            <div className="db-canvas-empty">
              <LayoutDashboard size={40} style={{ color: '#d4d4d8' }} />
              <h3>Tu dashboard aparecerá aquí</h3>
              <p>Usa el chat para agregar gráficos, tablas y métricas.</p>
              <button
                className="db-expert-btn"
                onClick={handleExpertDashboard}
                disabled={isStreaming || expertProgress}
                data-testid="expert-dashboard-btn"
              >
                <Sparkles size={16} />
                Generar Dashboard Experto
              </button>
              <span className="db-expert-hint">Genera 6 widgets automáticamente con datos reales</span>
            </div>
          ) : (
            <div className="db-widget-grid">
              {widgets.map((w, i) => (
                <DashboardWidget 
                  key={w.id || i} 
                  widget={w} 
                  index={i} 
                  onRemove={handleRemoveWidget}
                  googleCredentials={googleCredentials}
                  onGoogleAuth={handleGoogleAuth}
                  onChatMessage={addChatMessage}
                  ensureWorkspaceFolder={ensureWorkspaceFolder}
                />
              ))}
            </div>
          )}
        </div>

        {/* Chat Panel */}
        <div className="db-chat" data-testid="dashboard-chat">
          <div className="db-chat-header">
            <MessageSquare size={16} />
            <span>Chat</span>
          </div>

          <div className="db-chat-messages">
            {messages.length === 0 ? (
              <div className="db-chat-empty">
                <p className="db-chat-empty-title">Construye tu dashboard con IA</p>
                <button
                  className="db-expert-btn-chat"
                  onClick={handleExpertDashboard}
                  disabled={isStreaming || expertProgress}
                  data-testid="expert-dashboard-btn-chat"
                >
                  <Sparkles size={14} />
                  Dashboard Experto (6 widgets)
                </button>
                <p className="db-chat-divider">o pide widgets individuales</p>
                <div className="db-suggestions">
                  {suggestions.map((s, i) => (
                    <button key={i} className="db-suggestion" onClick={() => handleSend(s)} data-testid="dashboard-suggestion">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`db-msg ${m.role}`}>
                  {m.role === 'assistant' && isStreaming && i === messages.length - 1 && thinkingSteps.length > 0 && (
                    <MiniThinking steps={thinkingSteps} isActive={!m.content} />
                  )}
                  {m.content && (
                    <div className="db-msg-text chat-markdown">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />
                        }}
                      >
                        {m.content}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              ))
            )}
            {error && <div className="db-error">{error}</div>}
            <div ref={messagesEndRef} />
          </div>

          <div className="db-chat-input">
            <div className="db-input-wrap">
              <textarea
                ref={textareaRef}
                className="db-textarea"
                data-testid="dashboard-chat-input"
                placeholder="Describe el widget que quieres agregar..."
                value={inputValue}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={isStreaming}
              />
              <button
                className="db-send"
                data-testid="dashboard-chat-send"
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isStreaming}
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expert Progress Indicator */}
      <AnimatePresence>
        {expertProgress && (
          <motion.div
            className="db-expert-progress-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="db-expert-progress-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="db-expert-progress-header">
                <Sparkles size={20} style={{ color: '#0ea5e9' }} />
                <h3>Generando Dashboard Experto</h3>
              </div>
              
              <div className="db-expert-progress-body">
                <div className="db-expert-progress-bar-bg">
                  <motion.div
                    className="db-expert-progress-bar-fill"
                    initial={{ width: '0%' }}
                    animate={{ width: `${(expertProgress.current / expertProgress.total) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                
                <div className="db-expert-progress-text">
                  {expertProgress.status === 'starting' && (
                    <span>Preparando generación...</span>
                  )}
                  {expertProgress.status === 'generating' && (
                    <>
                      <span className="db-expert-progress-spinner" />
                      <span>Widget {expertProgress.current} de {expertProgress.total}: {expertProgress.name}</span>
                    </>
                  )}
                  {expertProgress.status === 'error' && (
                    <span style={{ color: '#ef4444' }}>
                      Error en widget {expertProgress.current}: {expertProgress.name}. Continuando...
                    </span>
                  )}
                  {expertProgress.status === 'completed' && (
                    <span style={{ color: '#22c55e' }}>
                      ✓ Dashboard completado con {expertProgress.total} widgets
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
